import React, { useEffect, useRef, useState } from 'react';
import { bonusAPI } from '../../../services';
import { Button, LoadingSpinner, ErrorMessage, SuccessMessage } from '../../UI';
import { Lock, Unlock, UserX, RotateCcw, X } from 'lucide-react';

const CYCLES = [
  { key: 'sep-oct', label: 'Sep – Oct cycle' },
  { key: 'mar-apr', label: 'Mar – Apr cycle' },
];

const RULES = {
  full: {
    label: 'Full Salary Credit',
    desc: 'Bonus = full Basic + DA, for every eligible employee',
    active: 'border-amber-500 bg-amber-50',
    dot: 'border-amber-500 bg-amber-500',
    ring: 'border-amber-500',
    text: 'text-amber-700',
  },
  statutory: {
    label: 'Statutory 8.33%',
    desc: 'Bonus = min(Salary, ₹7,000) × 8.33%, for every eligible employee',
    active: 'border-primary-500 bg-primary-50',
    dot: 'border-primary-500 bg-primary-500',
    ring: 'border-primary-500',
    text: 'text-primary-700',
  },
};

// Payment of Bonus Act, Section 9 grounds — must match DISQUALIFICATION_GROUNDS on the backend
const GROUNDS = [
  { value: 'fraud', label: 'Fraud' },
  { value: 'riotous_violent', label: 'Riotous or violent behaviour on premises' },
  { value: 'theft', label: 'Theft of company property' },
  { value: 'misappropriation', label: 'Misappropriation' },
  { value: 'sabotage', label: 'Sabotage' },
];

// Payslip month the cycle's bonus is credited in — must match PAYOUT_MONTH on the backend
const PAYOUT_MONTH_LABEL = { 'sep-oct': 'October', 'mar-apr': 'April' };

const fmtINR = (n) => '₹' + Math.round(n || 0).toLocaleString('en-IN');

// Default to the cycle we're currently in: Sep onwards → Sep–Oct, otherwise Mar–Apr
const defaultCycle = () => {
  const now = new Date();
  return { year: now.getFullYear(), cycle: now.getMonth() >= 8 ? 'sep-oct' : 'mar-apr' };
};

const BonusPage = () => {
  const initial = defaultCycle();
  const [year, setYear] = useState(initial.year);
  const [cycle, setCycle] = useState(initial.cycle);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDisqualified, setShowDisqualified] = useState(true);
  const [disqualifyTarget, setDisqualifyTarget] = useState(null);
  // Guards against a slower response for a previously selected year/cycle overwriting the current one
  const requestSeq = useRef(0);

  const yearOptions = [initial.year + 1, initial.year, initial.year - 1, initial.year - 2];

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, cycle]);

  const load = async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    try {
      const res = await bonusAPI.getCycle(year, cycle);
      if (seq === requestSeq.current) setData(res.data);
    } catch (err) {
      if (seq === requestSeq.current) setError(err.message || 'Failed to load bonus cycle');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  const run = async (key, fn, message) => {
    setSaving(key);
    setError('');
    setSuccess('');
    try {
      const res = await fn();
      requestSeq.current++; // a mutation result supersedes any in-flight load
      setData(res.data);
      if (message) setSuccess(message);
      return true;
    } catch (err) {
      setError(err.message || 'Something went wrong');
      return false;
    } finally {
      setSaving('');
    }
  };

  const isFinalized = data?.status === 'finalized';
  const rows = data?.rows || [];
  const filtered = showDisqualified ? rows : rows.filter((r) => !r.disqualified);
  const totals = data?.totals || { totalPayout: 0, eligibleCount: 0, disqualifiedCount: 0 };

  const changeRule = (rule) => {
    if (isFinalized || rule === data?.rule || saving) return;
    run('rule', () => bonusAPI.updateRule(year, cycle, rule));
  };

  const finalize = () => {
    if (!window.confirm(`Finalize ${fmtINR(totals.totalPayout)} bonus payout for ${totals.eligibleCount} employees? Amounts will be frozen and added to the ${PAYOUT_MONTH_LABEL[cycle]} ${year} payslip.`)) return;
    run('finalize', () => bonusAPI.finalize(year, cycle), 'Bonus cycle finalized');
  };

  const reopen = () => {
    if (!window.confirm('Reopen this cycle? Amounts will be recomputed from current salaries.')) return;
    run('reopen', () => bonusAPI.reopen(year, cycle), 'Bonus cycle reopened');
  };

  const reinstate = (row) => {
    run(`row-${row.employee}`, () => bonusAPI.reinstate(year, cycle, row.employee), `${row.name} reinstated`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <div className="text-xs text-secondary-500 mb-1">Payroll</div>
          <h1 className="text-2xl font-bold text-secondary-900">Statutory Bonus</h1>
          <p className="text-sm text-secondary-600 mt-1">
            One payout rule applies to all employees. Disqualified employees are excluded regardless of rule.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border border-secondary-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="flex border border-secondary-300 rounded-lg overflow-hidden bg-white">
            {CYCLES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCycle(c.key)}
                className={`px-4 py-2 text-sm ${cycle === c.key
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-secondary-600 hover:bg-secondary-50'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {loading && !data ? (
        <div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
      ) : data && (
        <>
          {/* Rule selector — the one HR-facing decision */}
          <div className="bg-white border border-secondary-200 rounded-xl p-4">
            <div className="flex justify-between items-center mb-3">
              <div className="text-xs text-secondary-500">Payout rule for this cycle</div>
              {isFinalized && (
                <span className="inline-flex items-center gap-1 text-xs text-secondary-500">
                  <Lock className="w-3 h-3" /> Locked — reopen the cycle to change
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(RULES).map(([key, r]) => {
                const selected = data.rule === key;
                return (
                  <button
                    key={key}
                    onClick={() => changeRule(key)}
                    disabled={isFinalized || !!saving}
                    className={`flex-1 min-w-[240px] text-left px-4 py-3 rounded-lg border-2 transition-colors disabled:cursor-not-allowed ${
                      selected ? r.active : 'border-secondary-200 bg-secondary-50 hover:border-secondary-300'
                    } ${isFinalized && !selected ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2.5 h-2.5 rounded-full border-2 ${selected ? r.dot : r.ring}`} />
                      <span className={`font-semibold text-sm ${selected ? r.text : 'text-secondary-900'}`}>{r.label}</span>
                      {saving === 'rule' && selected && <LoadingSpinner size="sm" />}
                    </div>
                    <div className="text-xs text-secondary-600 ml-[18px]">{r.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCard label="Total payout" value={fmtINR(totals.totalPayout)} valueClass="text-amber-700 font-mono" />
            <SummaryCard label="Eligible employees" value={totals.eligibleCount} valueClass="text-secondary-900" />
            <SummaryCard
              label="Disqualified (Sec. 9)"
              value={totals.disqualifiedCount}
              valueClass={totals.disqualifiedCount ? 'text-danger-600' : 'text-secondary-400'}
              small="Excluded from payout regardless of rule"
            />
          </div>

          {/* Status + actions */}
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div className="flex items-center gap-3">
              {isFinalized ? (
                <>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
                    <Lock className="w-3 h-3" /> Finalized
                    {data.finalizedAt && ` on ${new Date(data.finalizedAt).toLocaleDateString('en-IN')}`}
                  </span>
                  <Button variant="outline" size="sm" onClick={reopen} loading={saving === 'reopen'} disabled={!!saving}>
                    <Unlock className="w-4 h-4 mr-1" /> Reopen
                  </Button>
                </>
              ) : (
                <>
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">Draft</span>
                  <Button size="sm" onClick={finalize} loading={saving === 'finalize'} disabled={!!saving || rows.length === 0}>
                    <Lock className="w-4 h-4 mr-1" /> Finalize payout
                  </Button>
                </>
              )}
              <span className="text-xs text-secondary-500">
                {isFinalized ? 'Credited' : 'Once finalized, credited'} in the {PAYOUT_MONTH_LABEL[cycle]} {year} payslip
                {isFinalized ? ' (regenerate it if already generated)' : ''}
              </span>
            </div>
            <label className="flex items-center gap-2 text-sm text-secondary-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showDisqualified}
                onChange={(e) => setShowDisqualified(e.target.checked)}
                className="accent-red-600"
              />
              Show disqualified employees
            </label>
          </div>

          {/* Table */}
          <div className="bg-white border border-secondary-200 rounded-xl overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-secondary-200 text-xs text-secondary-500 text-left">
                  <th className="px-4 py-2.5 font-medium">ID</th>
                  <th className="px-4 py-2.5 font-medium">Employee</th>
                  <th className="px-4 py-2.5 font-medium">Department</th>
                  <th className="px-4 py-2.5 font-medium text-right">Basic+DA</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium text-right">Bonus</th>
                  {!isFinalized && <th className="px-4 py-2.5 font-medium text-right">Action</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-secondary-500">No employees for this cycle.</td>
                  </tr>
                )}
                {filtered.map((r) => (
                  <tr
                    key={r.employee}
                    className={`border-b border-secondary-100 last:border-0 ${r.disqualified ? 'bg-red-50' : ''}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-secondary-500">{r.employeeId || '—'}</td>
                    <td className="px-4 py-3 font-medium text-secondary-900">{r.name}</td>
                    <td className="px-4 py-3 text-secondary-600">{r.department || '—'}</td>
                    <td className="px-4 py-3 font-mono text-right text-secondary-600">
                      {fmtINR(r.salary)}
                      {r.salary === 0 && <div className="text-[11px] font-sans text-amber-600">No salary set</div>}
                    </td>
                    <td className={`px-4 py-3 text-xs ${r.disqualified ? 'text-danger-600' : 'text-secondary-500'}`}>
                      {r.disqualified ? r.reason : r.terminated ? 'Eligible (terminated)' : 'Eligible'}
                    </td>
                    <td
                      className={`px-4 py-3 font-mono text-right font-medium ${
                        r.disqualified ? 'text-secondary-400 line-through' : 'text-secondary-900'
                      }`}
                    >
                      {fmtINR(r.bonus)}
                    </td>
                    {!isFinalized && (
                      <td className="px-4 py-3 text-right">
                        {r.disqualified ? (
                          <button
                            onClick={() => reinstate(r)}
                            disabled={!!saving}
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 disabled:opacity-50"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Reinstate
                          </button>
                        ) : (
                          <button
                            onClick={() => setDisqualifyTarget(r)}
                            disabled={!!saving}
                            className="inline-flex items-center gap-1 text-xs text-danger-600 hover:text-danger-800 disabled:opacity-50"
                          >
                            <UserX className="w-3.5 h-3.5" /> Disqualify
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-secondary-500 leading-relaxed">
            Salary basis = Basic + Dearness Allowance only. The selected rule (Full Salary or Statutory 8.33%) applies
            uniformly to every eligible employee in this cycle. Employees dismissed for fraud, riotous/violent conduct,
            theft, misappropriation, or sabotage (Section 9) are disqualified and always carry zero payout, independent
            of the rule chosen above.
          </p>
        </>
      )}

      {disqualifyTarget && (
        <DisqualifyModal
          employee={disqualifyTarget}
          saving={saving === 'disqualify'}
          onClose={() => setDisqualifyTarget(null)}
          onSubmit={async ({ ground, reason }) => {
            const ok = await run(
              'disqualify',
              () => bonusAPI.disqualify(year, cycle, { employeeId: disqualifyTarget.employee, ground, reason }),
              `${disqualifyTarget.name} disqualified`
            );
            if (ok) setDisqualifyTarget(null);
          }}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, valueClass, small }) => (
  <div className="bg-white border border-secondary-200 rounded-xl p-4">
    <div className="text-xs text-secondary-500 mb-2">{label}</div>
    <div className={`text-xl font-semibold ${valueClass}`}>{value}</div>
    {small && <div className="text-[11px] text-secondary-400 mt-1">{small}</div>}
  </div>
);

const DisqualifyModal = ({ employee, saving, onClose, onSubmit }) => {
  const [ground, setGround] = useState('');
  const [reason, setReason] = useState('');

  const pickGround = (value) => {
    setGround(value);
    // Pre-fill the reason with the ground label; HR can make it more specific
    const label = GROUNDS.find((g) => g.value === value)?.label || '';
    if (!reason || GROUNDS.some((g) => g.label === reason)) setReason(label);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center px-5 py-4 border-b border-secondary-200">
          <h3 className="font-semibold text-secondary-900">Disqualify {employee.name}</h3>
          <button onClick={onClose} className="text-secondary-400 hover:text-secondary-600"><X className="w-5 h-5" /></button>
        </div>
        <form
          className="px-5 py-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (ground && reason.trim()) onSubmit({ ground, reason: reason.trim() });
          }}
        >
          <p className="text-sm text-secondary-600">
            Only employees dismissed for one of the Section 9 grounds below can be disqualified. Their bonus for this
            cycle will be zero.
          </p>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">Ground (Section 9)</label>
            <select
              value={ground}
              onChange={(e) => pickGround(e.target.value)}
              required
              className="w-full border border-secondary-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select a ground…</option>
              {GROUNDS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">Reason</label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              maxLength={200}
              className="w-full border border-secondary-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Shown in the bonus register"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="danger" loading={saving} disabled={!ground || !reason.trim()}>Disqualify</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BonusPage;
